import React, { useEffect, useMemo, useState } from 'react';

import { AutoCompleteSelectEvent } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useGetPredictionTechsQuery } from '@/app/services/equipment';
import { useGetFreezeStorageLocationsQuery } from '@/app/services/locations';
import { addToast } from '@/app/slices/toast';
import { StorageLocationAutoComplete } from '@/components/common';

import { useFreezeContext } from '../hooks/useFreezeContext';
import { FREEZE_DAY_OPTIONS } from '../utils/constants';
import { getFertilizationSiteId } from '../utils/utils';
import { FreezeEditBar } from './FreezeEditBar';

import './FreezeToolbar.css';

type FreezeToolbarProps = {
  className?: string;
  showEditView?: boolean;
};

export const FreezeToolbar = ({
  className,
  showEditView,
}: FreezeToolbarProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { form, setForm } = useFreezeContext();

  const [localForm, setLocalForm] = useState(form);
  const [outsideFreeze, setOutsideFreeze] = useState(false);

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(localForm) !== JSON.stringify(form);
  }, [localForm, form]);

  const isSaved = useMemo(() => {
    return !hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    setLocalForm(form);
  }, [form]);

  const [params] = useSearchParams();
  const siteId = params.get('sId');

  const { data: techsData } = useGetPredictionTechsQuery({
    where: {
      or: [{ farmlocation: { eq: false } }, { farmlocation: { eq: null } }],
    },
  });
  const { data: storageLocations } = useGetFreezeStorageLocationsQuery(
    getFertilizationSiteId(siteId),
  );

  const handleSave = () => {
    setForm(localForm);
    dispatch(
      addToast({
        severity: 'success',
        summary: t('common.success'),
        detail: t('qc.formSettingsSavedSuccessfully'),
        sticky: false,
      }),
    );
  };

  return (
    <div className={`freeze-toolbar ${className || ''}`}>
      <div className="freeze-toolbar__row">
        <div className="label-input-group">
          <label htmlFor="mating-name">{t('freeze.outsideFreeze')}</label>
          <Checkbox
            id="mating-name"
            checked={outsideFreeze as boolean}
            onChange={(e) => {
              setOutsideFreeze(e.checked as boolean);
            }}
          />
        </div>
        <div className="label-input-group">
          {outsideFreeze ? (
            <StorageLocationAutoComplete
              label={t('storageLocation.storageLocation')}
              value={localForm.storageLocation}
              field="name"
              onSelect={(e: AutoCompleteSelectEvent) =>
                setLocalForm({
                  storageLocation: e.value.id,
                  freezeLocation: e.value.siteId,
                })
              }
              appendTo={null}
            />
          ) : (
            <>
              <label>{t('storageLocation.storageLocation')}</label>
              <Dropdown
                options={[
                  {
                    label: t('storageLocation.selectStorageLocation'),
                    value: '0',
                  },
                  ...(storageLocations || []).map((location) => ({
                    label: location.name,
                    value: location.id,
                  })),
                ]}
                value={localForm.storageLocation}
                onChange={(e) => {
                  setLocalForm({ ...localForm, storageLocation: e.value });
                }}
                placeholder={t('storageLocation.selectStorageLocation')}
                className="storage-location"
                scrollHeight="34rem"
                panelStyle={{ height: '40rem' }}
                virtualScrollerOptions={{
                  itemSize: 25,
                }}
                filter
                filterBy="label"
              />
            </>
          )}
        </div>

        <div className="label-input-group">
          <label>{t('freeze.freezeDay')}</label>
          <Dropdown
            options={FREEZE_DAY_OPTIONS}
            value={localForm.freezeDay}
            onChange={(e) => {
              setLocalForm({ ...localForm, freezeDay: e.value });
            }}
            placeholder={t('freeze.selectFreezeDay')}
          />
        </div>

        <div className="label-input-group">
          <label>{t('tech.predictionTech')}</label>
          <Dropdown
            value={localForm.predictionTech}
            options={(techsData || []).map((tech) => ({
              label: tech.name,
              value: tech.id,
            }))}
            onChange={(e) => {
              setLocalForm({
                ...localForm,
                predictionTech: e.value === '' ? null : e.value,
              });
            }}
            filter
            filterBy="label"
            virtualScrollerOptions={{
              itemSize: 38,
            }}
            placeholder={t('tech.predictionTech')}
          />
        </div>

        <div className="label-input-group">
          <label>{t('grading.grader')}</label>
          <Dropdown
            value={localForm.grader}
            options={(techsData || []).map((tech) => ({
              label: tech.name,
              value: tech.id,
            }))}
            onChange={(e) => {
              setLocalForm({
                ...localForm,
                grader: e.value === '' ? null : e.value,
              });
            }}
            filter
            filterBy="label"
            virtualScrollerOptions={{
              itemSize: 38,
            }}
            placeholder={t('grading.grader')}
          />
        </div>

        <div className="label-input-group">
          <label>{t('freeze.freezeTech')}</label>
          <Dropdown
            value={localForm.freezeTech}
            options={(techsData || []).map((tech) => ({
              label: tech.name,
              value: tech.id,
            }))}
            onChange={(e) => {
              setLocalForm({
                ...localForm,
                freezeTech: e.value === '' ? null : e.value,
              });
            }}
            filter
            placeholder={t('freeze.freezeTech')}
            virtualScrollerOptions={{
              itemSize: 38,
            }}
          />
        </div>

        <div className="label-input-group">
          <label>{t('freeze.freezer')}</label>
          <InputText
            value={localForm.freezer || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setLocalForm({ ...localForm, freezer: e.target.value });
            }}
          />
        </div>
        <div className="label-input-group">
          <label>{t('embryo.embryosPerVitStraw')}</label>
          <InputNumber
            value={localForm.embryosPerVitStraw}
            onValueChange={(e) => {
              setLocalForm({ ...localForm, embryosPerVitStraw: e.value || 0 });
            }}
            min={0}
            showButtons
          />
        </div>

        <div className="label-input-group">
          <label>{t('embryo.embryosPerStraw')}</label>
          <InputNumber
            value={localForm.embryosPerStraw}
            onValueChange={(e) => {
              setLocalForm({ ...localForm, embryosPerStraw: e.value || 0 });
            }}
            min={0}
            showButtons
          />
        </div>

        <div className="label-input-group">
          <label>{t('freeze.dtPlus')}</label>
          <Checkbox
            checked={localForm.dt as boolean}
            onChange={(e) => {
              setLocalForm({ ...localForm, dt: e.checked as boolean });
            }}
          />
        </div>
        <div className="label-input-group">
          <label htmlFor="save-button">&nbsp;</label>
          <Button
            id="save-button"
            icon={isSaved ? 'pi pi-check' : 'pi pi-save'}
            onClick={handleSave}
            tooltip={
              isSaved ? t('common.changesSaved') : t('common.saveChanges')
            }
            tooltipOptions={{ position: 'top' }}
            severity={isSaved ? 'success' : 'secondary'}
            disabled={!hasUnsavedChanges}
          />
        </div>
      </div>

      {showEditView && <FreezeEditBar />}
    </div>
  );
};
